import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import NoChartDataView from "../no-chart-data-view/no-chart-data-view";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface IProps {
  borderColor1: string;
  borderColor2?: string;
  borderColor3?: string;
  borderColor4?: string;
  backgroundColor1: string;
  backgroundColor2?: string;
  backgroundColor3?: string;
  backgroundColor4?: string;
  label1: string;
  label2?: string;
  label3?: string;
  label4?: string;
  data1: Array<number | null>;
  data2?: Array<number | null>;
  data3?: Array<number | null>;
  data4?: Array<number | null>;
  labels: Array<string | number>;
  yAxisUnitLeftSide: string;
  yAxisUnitRightSide?: string;
  xAxisLabel?: string;
  labelCallback?: (tooltipItem: any) => string;
  titleCallback?: (tooltipItems: any[]) => string;
  xLabelCallback?: (
    value: string | number,
    index: number,
    values: any
  ) => string;
  isChartDataAvailable?: boolean;
}

const MultipleChartType = (props: IProps) => {
  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: "top" as const,
        align: "start" as const,
        labels: {
          usePointStyle: true,
          pointStyleWidth: 10,
          boxHeight: 7,
          font: {
            size: 14,
            family: "Poppins",
            weight: "500" as const,
          },
          color: "#383941",
          generateLabels: (chart: any) => {
            const original =
              ChartJS.defaults.plugins.legend.labels.generateLabels;
            const labels = original(chart);
            labels.forEach((label: any) => {
              switch (label.datasetIndex) {
                case 0:
                  label.pointStyle = "circle";
                  label.fillStyle = props.borderColor1;
                  break;
                case 1:
                  label.pointStyle = "circle";
                  label.fillStyle = props.borderColor2;
                  break;
                case 2:
                  label.pointStyle = "circle";
                  label.fillStyle = props.borderColor3;
                  break;
                case 3:
                  label.pointStyle = "circle";
                  label.fillStyle = props.borderColor4;
                  break;
                default:
                  label.pointStyle = "circle";
                  label.fillStyle = "";
                  break;
              }
            });
            return labels;
          },
        },
      },
      datalabels: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: props.titleCallback,
          label: props.labelCallback,
        },
      },
      centerText: false,
    },
    scales: {
      x: {
        title: {
          text: props.xAxisLabel,
          display: props.xAxisLabel ? true : false,
          color: "#69768B",
          font: {
            family: "Poppins",
            size: 12,
            weight: "500",
          },
        },
        ticks: {
          callback: props.xLabelCallback,
        },
        grid: {
          display: false,
        },
      },
      "left-y-axis": {
        type: "linear" as const,
        position: "left" as const,
        beginAtZero: true,
        ticks: {
          color: "#69768B",
          callback: (value: any) => `${value} ${props.yAxisUnitLeftSide}`,
        },
      },
      ...(props.yAxisUnitRightSide && {
        "right-y-axis": {
          type: "linear" as const,
          position: "right" as const,
          beginAtZero: true,
          grid: {
            drawOnChartArea: false,
          },
          ticks: {
            color: "#69768B",
            callback: (value: any) => `${value} ${props.yAxisUnitRightSide}`,
          },
        },
      }),
    },
  };

  const legendMargin = {
    id: "legendMargin",
    beforeInit: (chart: any) => {
      const fitValue = chart.legend.fit;
      chart.legend.fit = () => {
        fitValue.bind(chart.legend)();
        return (chart.legend.height += 30);
      };
    },
  };

  const data = {
    labels: props.labels,
    datasets: [
      {
        type: "bar" as const,
        label: props.label1,
        data: props.data1,
        borderColor: props.borderColor1,
        backgroundColor: props.backgroundColor1,
        yAxisID: "left-y-axis",
      },
      {
        type: "bar" as const,
        label: props.label2,
        data: props.data2,
        borderColor: props.borderColor2,
        backgroundColor: props.backgroundColor2,
        yAxisID: "left-y-axis",
      },
      {
        type: "bar" as const,
        label: props.label3,
        data: props.data3,
        borderColor: props.borderColor3,
        backgroundColor: props.backgroundColor3,
        yAxisID: "left-y-axis",
      },
      {
        type: "line" as const,
        label: props.label4,
        data: props.data4,
        borderColor: props.borderColor4,
        backgroundColor: props.backgroundColor4,
        fill: true,
        yAxisID: "right-y-axis",
      },
    ],
  };

  return (
    <div
      style={{
        width: "100%",
        overflowX: "auto",
        overflowY: "hidden",
        height: "100%",
      }}
    >
      <div
        style={{
          minWidth: "100%",
          width: "1000px",
          minHeight: props.isChartDataAvailable
            ? "calc(100vh - 300px)"
            : "calc(100vh - 190px)",
          alignItems: "center",
          justifyContent: "center",
          display: "flex",
        }}
      >
        {props.isChartDataAvailable ? (
          <Chart
            type="bar"
            data={data}
            options={options}
            plugins={[legendMargin]}
          />
        ) : (
          <NoChartDataView />
        )}
      </div>
    </div>
  );
};

export default MultipleChartType;
